import { useEffect, useState } from "react";
import { api, formatApiError, money, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal, Field, inputClass, PrimaryButton, EmptyState } from "../components/common";
import { toast } from "sonner";
import { ClipboardText, Plus, PencilSimple, Trash, CalendarBlank, Receipt, CheckCircle, FileText } from "@phosphor-icons/react";

const EMPTY = { title: "", contractor_id: "", amount: "", start_date: "", end_date: "", progress: 0, status: "activo", description: "" };
const STATUS = [
  { value: "activo", label: "Activa" },
  { value: "pausada", label: "Pausada" },
  { value: "terminada", label: "Terminada" },
  { value: "cancelada", label: "Cancelada" },
];
const statusTone = { activo: "bg-emerald-50 text-emerald-700", pausada: "bg-amber-50 text-amber-700", terminada: "bg-blue-50 text-blue-700", cancelada: "bg-slate-100 text-slate-600" };

export default function WorkOrders() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const canDelete = ["admin", "gerente_general", "gerente_area"].includes(user?.role) || !!user?.can_delete;

  const [payFor, setPayFor] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payForm, setPayForm] = useState({ amount: "", period: "", concept: "", paidNow: false });
  const [payFiles, setPayFiles] = useState({});
  const [payBusy, setPayBusy] = useState(false);
  const loadPayments = (oid) => api.get(`/work-orders/${oid}/payments`).then((r) => setPayments(r.data)).catch(() => {});
  const openPayments = (o) => { setPayFor(o); setPayForm({ amount: "", period: "", concept: "", paidNow: false }); setPayFiles({}); loadPayments(o.id); };
  const addPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount) return toast.error("Escribe el monto del pago");
    setPayBusy(true);
    try {
      await api.post(`/work-orders/${payFor.id}/payments`, { amount: parseFloat(payForm.amount) || 0, period: payForm.period, concept: payForm.concept, status: payForm.paidNow ? "pagado" : "por_pagar" });
      toast.success("Pago registrado"); setPayForm({ amount: "", period: "", concept: "", paidNow: false }); loadPayments(payFor.id); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPayBusy(false); }
  };
  const markPaid = async (pid) => {
    const fd = new FormData();
    if (payFiles[pid]) fd.append("file", payFiles[pid]);
    try { await api.post(`/work-orders/${payFor.id}/payments/${pid}/pay`, fd); toast.success("Marcado como pagado"); loadPayments(payFor.id); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delPayment = async (pid) => { if (!window.confirm("¿Eliminar este pago?")) return; try { await api.delete(`/work-orders/${payFor.id}/payments/${pid}`); loadPayments(payFor.id); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const payTotal = payments.filter((p) => p.status === "pagado").reduce((a, p) => a + (p.amount || 0), 0);

  const load = () => api.get("/work-orders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/contractors").then((r) => setContractors(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => {
    setEditId(o.id);
    setForm({ title: o.title || "", contractor_id: o.contractor_id || "", amount: o.amount || "", start_date: o.start_date || "", end_date: o.end_date || "", progress: o.progress || 0, status: o.status || "activo", description: o.description || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, amount: parseFloat(form.amount) || 0, progress: parseFloat(form.progress) || 0, contractor_id: form.contractor_id || null };
    try {
      if (editId) { await api.put(`/work-orders/${editId}`, payload); toast.success("Orden de trabajo actualizada"); }
      else { await api.post("/work-orders", payload); toast.success("Orden de trabajo creada"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar esta orden de trabajo?")) return; try { await api.delete(`/work-orders/${id}`); toast.success("Orden eliminada"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader overline="Planeación de obra" title="Órdenes de Trabajo"
        action={<PrimaryButton onClick={openNew} data-testid="add-workorder-btn"><Plus size={18} weight="bold" /> Nueva orden</PrimaryButton>} />

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-indigo-500">
        Las órdenes de trabajo son actividades internas o trabajos por administración que <b className="text-foreground">no dependen de un contrato</b>. Al capturar fechas de inicio y término aparecen en el <b className="text-foreground">Programa de Obra</b> en un color distinto al de los contratos.
      </Card>

      {items.length === 0 ? (
        <EmptyState icon={ClipboardText} title="Sin órdenes de trabajo" subtitle="Crea órdenes de trabajo con sus fechas para verlas en el Programa de Obra."
          action={<PrimaryButton onClick={openNew}><Plus size={18} weight="bold" /> Nueva orden</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((o) => (
            <Card key={o.id} data-testid={`workorder-${o.id}`} className="p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold leading-tight">{o.title}</div>
                  <div className="text-sm text-muted-foreground">{o.contractor_name || "Sin contratista"}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold flex-shrink-0 ${statusTone[o.status] || "bg-slate-100"}`}>{(STATUS.find((s) => s.value === o.status) || {}).label || o.status}</span>
              </div>
              {(o.start_date || o.end_date) && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2"><CalendarBlank size={13} weight="bold" /> {o.start_date || "—"} → {o.end_date || "—"}</div>
              )}
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Avance</span><span className="font-semibold">{o.progress || 0}%</span></div>
                <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${Math.min(100, o.progress || 0)}%` }} /></div>
              </div>
              {o.amount > 0 && <div className="text-sm mt-3">Monto: <b>{money(o.amount)}</b></div>}
              {o.total_paid > 0 && <div className="text-sm text-success">Pagado: <b>{money(o.total_paid)}</b></div>}
              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border">
                <button onClick={() => openEdit(o)} data-testid={`edit-workorder-${o.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><PencilSimple size={15} weight="bold" /> Editar</button>
                <button onClick={() => openPayments(o)} data-testid={`payments-workorder-${o.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:underline"><Receipt size={15} weight="bold" /> Pagos</button>
                {canDelete && <button onClick={() => remove(o.id)} data-testid={`delete-workorder-${o.id}`} className="ml-auto p-1 text-muted-foreground hover:text-danger"><Trash size={16} /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar orden de trabajo" : "Nueva orden de trabajo"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Título / descripción *"><input required data-testid="wo-title" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Limpieza y desmonte de terreno" /></Field>
          <Field label="Contratista / responsable (opcional)">
            <select data-testid="wo-contractor" className={inputClass} value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
              <option value="">Interno / sin contratista</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio"><input type="date" data-testid="wo-start" className={inputClass} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Término"><input type="date" data-testid="wo-end" className={inputClass} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Avance (%)"><input type="number" min="0" max="100" data-testid="wo-progress" className={inputClass} value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} /></Field>
            <Field label="Monto (opcional)"><input type="number" min="0" step="0.01" data-testid="wo-amount" className={inputClass} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
          </div>
          <Field label="Estatus">
            <select data-testid="wo-status" className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Notas (opcional)"><textarea data-testid="wo-description" className={inputClass} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-workorder-btn">{busy ? "Guardando…" : (editId ? "Guardar cambios" : "Crear orden")}</PrimaryButton>
        </form>
      </Modal>

      <Modal open={!!payFor} onClose={() => setPayFor(null)} title={`Pagos · ${payFor?.title || ""}`}>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">Registra pagos de esta orden de trabajo. El <b className="text-foreground">neto pagado</b> aparece automáticamente en <b className="text-foreground">Estado de Cuenta → Pagos consolidados</b> agrupado por contratista{payFor?.contractor_name && payFor.contractor_name !== "—" ? ` (${payFor.contractor_name})` : " (Interno)"}.</div>
          <form onSubmit={addPayment} className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end bg-muted/40 p-3 rounded-sm">
            <Field label="Monto *"><input type="number" min="0" step="0.01" data-testid="wo-pay-amount" className={inputClass} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></Field>
            <Field label="Fecha del periodo"><input type="date" data-testid="wo-pay-period" className={inputClass} value={payForm.period} onChange={(e) => setPayForm({ ...payForm, period: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" data-testid="wo-pay-paidnow" checked={payForm.paidNow} onChange={(e) => setPayForm({ ...payForm, paidNow: e.target.checked })} /> Marcar como pagado ahora</label>
            <PrimaryButton type="submit" disabled={payBusy} data-testid="wo-pay-add-btn" className="sm:col-span-2">{payBusy ? "Guardando…" : "Agregar pago"}</PrimaryButton>
          </form>
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin pagos registrados.</p>
          ) : (
            <div className="space-y-2">
              {payments.map((p) => (
                <div key={p.id} data-testid={`wo-payment-${p.id}`} className="flex flex-wrap items-center gap-2 border border-border rounded-sm p-2.5 text-sm">
                  <span className="font-semibold">#{p.number}</span>
                  <span className="text-muted-foreground">{p.period || "—"}</span>
                  <span className="font-heading font-bold">{money(p.amount)}</span>
                  <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${p.status === "pagado" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status === "pagado" ? "Pagado" : "Por pagar"}</span>
                  {p.receipt_path && <a href={fileUrl(p.receipt_path)} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 hover:underline"><FileText size={14} weight="fill" /> Comprobante</a>}
                  <div className="ml-auto flex items-center gap-2">
                    {p.status !== "pagado" && (
                      <>
                        <input type="file" data-testid={`wo-pay-file-${p.id}`} onChange={(e) => setPayFiles({ ...payFiles, [p.id]: e.target.files[0] })} className="text-xs w-36" />
                        <button onClick={() => markPaid(p.id)} data-testid={`wo-pay-mark-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-success rounded-sm px-2 py-1 hover:opacity-90"><CheckCircle size={13} weight="fill" /> Marcar pagado</button>
                      </>
                    )}
                    {canDelete && <button onClick={() => delPayment(p.id)} data-testid={`wo-pay-del-${p.id}`} className="p-1 text-muted-foreground hover:text-danger"><Trash size={15} /></button>}
                  </div>
                </div>
              ))}
              <div className="text-sm font-semibold text-right pt-1">Total pagado: <span className="text-success">{money(payTotal)}</span></div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
